import { userAtom } from '@/atoms/user.atom';
import { execute } from '@/graphql/execute';
import { useOrganizations } from '@/hooks/useOrganizations';
import { analytics } from '@/lib/analytics';
import { saveOnboardingSurveyMutation } from '@/mutations/user.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { myProjectsQuery } from '@/queries/project.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { NextStepProvider, NextStepReact, useNextStep } from 'nextstepjs';
import { useReactRouterAdapter } from 'nextstepjs/adapters/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import OnboardingTourCard from './OnboardingTourCard';
import { buildTourSteps } from './tourSteps';

// Captures setCurrentStep from useNextStep and exposes it via a ref
const TourBridge = ({
  setCurrentStepRef,
}: {
  setCurrentStepRef: React.MutableRefObject<((step: number, delay?: number) => void) | null>;
}) => {
  const { startNextStep, setCurrentStep } = useNextStep();
  const [started, setStarted] = useState(false);

  setCurrentStepRef.current = setCurrentStep;

  useEffect(() => {
    if (!started) {
      const timer = setTimeout(() => {
        startNextStep('onboarding');
        setStarted(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [started, startNextStep]);

  return null;
};

export const OnboardingTourProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useAtomValue(userAtom);
  const { organization } = useOrganizations();
  const queryClient = useQueryClient();
  const { openDialog, dismissActiveDialog } = useDialog();
  const [, setParams] = useSearchParams();
  const completedRef = useRef(false);
  const setCurrentStepRef = useRef<((step: number, delay?: number) => void) | null>(null);

  const { data: projectsData } = useQuery({
    queryKey: ['projects', organization?.id],
    queryFn: () => execute(myProjectsQuery),
    enabled: !!organization?.id,
  });

  const firstProjectId = projectsData?.projects?.[0]?.id ?? null;
  const steps = useMemo(() => buildTourSteps(firstProjectId, openDialog), [firstProjectId, openDialog]);

  const shouldShowTour = !!(user && !user.onboardingTourCompleted && organization);

  const handleComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    try {
      await execute(saveOnboardingSurveyMutation, {
        data: { tourCompletedAt: new Date().toISOString() },
      });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      analytics.capture('Onboarding Tour Completed');
    } catch (err) {
      console.error('Failed to save tour completion:', err);
    }
  }, [queryClient]);

  const handleSkip = useCallback(
    async (step: number) => {
      if (completedRef.current) return;
      completedRef.current = true;
      try {
        await execute(saveOnboardingSurveyMutation, {
          data: { tourSkippedAt: new Date().toISOString(), tourSkippedAtStep: step },
        });
        queryClient.invalidateQueries({ queryKey: ['me'] });
        analytics.capture('Onboarding Tour Skipped', { step });
      } catch (err) {
        console.error('Failed to save tour skip:', err);
      }
    },
    [queryClient],
  );

  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const handleStepChange = useCallback(
    (stepIndex: number) => {
      const tourSteps = stepsRef.current[0]?.steps;
      if (!tourSteps) return;

      const stepKey = tourSteps[stepIndex]?.content as string;

      // Open the layers panel when reaching the layer sidebar step
      if (stepKey === 'layerSidebar') {
        const params = new URLSearchParams(window.location.search);
        if (params.get('layers') !== 'true') {
          params.set('layers', 'true');
          setParams(params);
        }
      }

      // Open the share dialog, then re-seat the step after the dialog mounts
      if (stepKey === 'projectSharing' && firstProjectId) {
        setCurrentStepRef.current?.(stepIndex, 2000);
      }

      // Close the share dialog when leaving the sharing step
      if (stepKey !== 'projectSharing') {
        dismissActiveDialog();
      }
    },
    [firstProjectId, setParams, openDialog, dismissActiveDialog],
  );

  if (!shouldShowTour) {
    return <>{children}</>;
  }

  return (
    <NextStepProvider>
      <NextStepReact
        steps={steps}
        navigationAdapter={useReactRouterAdapter}
        cardComponent={OnboardingTourCard}
        onComplete={handleComplete}
        onSkip={handleSkip}
        onStepChange={handleStepChange}
        shadowRgb='0, 0, 0'
        shadowOpacity='0.6'
        scrollToTop={false}
        noInViewScroll={true}>
        {children}
        <TourBridge setCurrentStepRef={setCurrentStepRef} />
      </NextStepReact>
    </NextStepProvider>
  );
};
